// Material 3 Design Tokens and Dynamic Color Theming System
// Aligned with Google Material Web (material-components/material-web) & Material You specification
(function () {
  const { createTheme } = MaterialUI;

  // --- Color Conversion & Mathematical Utilities ---
  function hexToRgb(hex) {
    if (!hex || typeof hex !== 'string') return { r: 103, g: 80, b: 164 };
    let c = hex.replace('#', '').trim();
    if (c.length === 3) c = c.split('').map(x => x + x).join('');
    if (c.length !== 6) return { r: 103, g: 80, b: 164 };
    const num = parseInt(c, 16);
    return {
      r: (num >> 16) & 255,
      g: (num >> 8) & 255,
      b: num & 255
    };
  }

  function rgbToHex(r, g, b) {
    const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));
    return '#' + [clamp(r), clamp(g), clamp(b)].map(x => x.toString(16).padStart(2, '0')).join('');
  }

  // --- Material 3 colors ---
  // Scheme generation (real HCT / Material You) lives in the shared M3Core bundle (/shared/m3-core.js),
  // so the portal, Notes and Telemetry all derive identical colors from the same seed.
  // Admin › Site settings: text fields (typed text, labels, helper text) and headings
  const FIELD_FONT = "var(--wb-font-fields, 'Google Sans', 'Google Sans Text', Roboto, sans-serif)";
  const HEADING_FONT = "var(--wb-font-heading, 'Google Sans', 'Google Sans Text', Roboto, sans-serif)";
  const M3_COLOR_SCHEMES = M3Core.PRESETS;

  function generateM3Colors(seedHex, mode = 'dark') {
    return M3Core.generateColors(seedHex, mode);
  }

  // Resolves a preset key or custom hex seed into the full role map
  function getM3Colors(mode = 'dark', seedOrKey = 'purple') {
    return M3Core.generateColors(seedOrKey, mode);
  }

  // --- Material 3 Shape Scale ---
  const m3Shape = {
    none: '0px',
    extraSmall: '4px',
    small: '8px',
    medium: '12px',
    large: '16px',
    extraLarge: '28px',
    full: '9999px'
  };

  // --- Material 3 Type Scale (size / line-height / weight / tracking) ---
  // Mirrored as CSS tokens in /shared/m3.css (--m3-type-*).
  const t = (size, line, weight, tracking) => ({
    fontSize: `${size / 16}rem`,
    lineHeight: `${line / size}`,
    fontWeight: weight,
    letterSpacing: `${tracking}px`
  });
  const m3Typescale = {
    displayLarge: t(57, 64, 400, -0.25),
    displayMedium: t(45, 52, 400, 0),
    displaySmall: t(36, 44, 400, 0),
    headlineLarge: t(32, 40, 400, 0),
    headlineMedium: t(28, 36, 400, 0),
    headlineSmall: t(24, 32, 400, 0),
    titleLarge: t(22, 28, 400, 0),
    titleMedium: t(16, 24, 500, 0.15),
    titleSmall: t(14, 20, 500, 0.1),
    labelLarge: t(14, 20, 500, 0.1),
    labelMedium: t(12, 16, 500, 0.5),
    labelSmall: t(11, 16, 500, 0.5),
    bodyLarge: t(16, 24, 400, 0.5),
    bodyMedium: t(14, 20, 400, 0.25),
    bodySmall: t(12, 16, 400, 0.4)
  };

  // --- Material 3 Elevation & Shadow Definitions ---
  const m3Elevation = {
    level0: 'none',
    level1: '0 1px 3px 1px rgba(0, 0, 0, 0.15), 0 1px 2px 0 rgba(0, 0, 0, 0.3)',
    level2: '0 2px 6px 2px rgba(0, 0, 0, 0.15), 0 1px 2px 0 rgba(0, 0, 0, 0.3)',
    level3: '0 4px 8px 3px rgba(0, 0, 0, 0.15), 0 1px 3px 0 rgba(0, 0, 0, 0.3)',
    level4: '0 6px 10px 4px rgba(0, 0, 0, 0.15), 0 2px 3px 0 rgba(0, 0, 0, 0.3)',
    level5: '0 8px 12px 6px rgba(0, 0, 0, 0.15), 0 4px 4px 0 rgba(0, 0, 0, 0.3)'
  };

  // --- Dynamic CSS Variables Injector ---
  function applyM3CssVariables(colors, mode = 'dark') {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;

    root.setAttribute('data-theme', mode);
    if (document.body) {
      document.body.setAttribute('data-theme', mode);
      document.body.style.backgroundColor = colors.surface;
      document.body.style.color = colors.onSurface;
    }

    for (const [key, val] of Object.entries(colors)) {
      if (typeof val === 'string') {
        const kebab = key.replace(/([A-Z])/g, '-$1').toLowerCase();
        root.style.setProperty(`--m3-${kebab}`, val);
      }
    }
    if (window.M3Core && window.M3Core.themeFavicon) window.M3Core.themeFavicon(colors); // the favicon follows the theme
  }

  // --- Resolves 'auto' mode using system preferences ---
  function resolveM3Mode(modePreference) {
    if (modePreference === 'auto') {
      if (typeof window !== 'undefined' && window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }
      return 'dark';
    }
    return modePreference === 'light' ? 'light' : 'dark';
  }

  // --- Dynamic MUI Theme Builder ---
  function getM3Theme(mode = 'dark', seedOrKey = 'purple') {
    const effectiveMode = resolveM3Mode(mode);
    const isDark = effectiveMode === 'dark';
    const colors = getM3Colors(effectiveMode, seedOrKey);

    const narrowView = !!(window.M3Core && window.M3Core.getA11y().narrow > 0);
    const built = createTheme({
      palette: {
        mode: effectiveMode,
        m3: colors,
        primary: {
          main: colors.primary,
          light: colors.onPrimaryContainer,
          dark: colors.primaryContainer,
          contrastText: colors.onPrimary
        },
        secondary: {
          main: colors.secondary,
          light: colors.onSecondaryContainer,
          dark: colors.secondaryContainer,
          contrastText: colors.onSecondary
        },
        tertiary: {
          main: colors.tertiary,
          light: colors.onTertiaryContainer,
          dark: colors.tertiaryContainer,
          contrastText: colors.onTertiary
        },
        error: {
          main: colors.error,
          light: colors.onErrorContainer,
          dark: colors.errorContainer,
          contrastText: colors.onError
        },
        background: {
          default: colors.surface,
          paper: colors.surfaceContainer
        },
        text: {
          primary: colors.onSurface,
          secondary: colors.onSurfaceVariant
        },
        divider: colors.outlineVariant
      },
      shape: {
        borderRadius: 4
      },
      typography: {
        // The fonts chosen in Admin › Site settings (--wb-font-*, written into the page head by site-settings.js; m3.css has defaults)
        fontFamily: "var(--wb-font-body, 'Google Sans', 'Google Sans Text', Roboto, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif)",
        // MUI variants mapped onto the M3 type scale; the headings take the heading font
        h1: { ...m3Typescale.displayLarge, fontFamily: HEADING_FONT },
        h2: { ...m3Typescale.displayMedium, fontFamily: HEADING_FONT },
        h3: { ...m3Typescale.displaySmall, fontFamily: HEADING_FONT },
        h4: { ...m3Typescale.headlineMedium, fontFamily: HEADING_FONT },
        h5: { ...m3Typescale.headlineSmall, fontFamily: HEADING_FONT },
        h6: { ...m3Typescale.titleLarge, fontFamily: HEADING_FONT },
        subtitle1: m3Typescale.titleMedium,
        subtitle2: m3Typescale.titleSmall,
        body1: m3Typescale.bodyLarge,
        body2: m3Typescale.bodyMedium,
        caption: m3Typescale.bodySmall,
        overline: { ...m3Typescale.labelMedium, textTransform: 'none' },
        button: { ...m3Typescale.labelLarge, textTransform: 'none' }
      },
      components: {
        MuiCard: {
          defaultProps: { elevation: 0 },
          styleOverrides: { root: { backgroundImage: 'none' } }
        },
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: m3Shape.full,
              padding: '10px 24px',
              fontSize: '0.875rem',
              fontWeight: 500,
              letterSpacing: '0.1px',
              textTransform: 'none'
            }
          }
        },
        MuiChip: {
          styleOverrides: {
            root: {
              borderRadius: m3Shape.small,
              fontWeight: 500
            }
          }
        },
        MuiMenu: {
          styleOverrides: {
            paper: {
              backgroundColor: colors.surfaceContainerHigh,
              backgroundImage: 'none',
              borderRadius: m3Shape.extraLarge,
              border: `1px solid ${colors.outlineVariant}`,
              boxShadow: isDark
                ? '0 12px 36px rgba(0, 0, 0, 0.55), 0 4px 12px rgba(0, 0, 0, 0.25)'
                : '0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.06)',
              padding: '8px'
            },
            list: {
              padding: 0
            }
          }
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              borderRadius: m3Shape.large,
              margin: '3px 4px',
              padding: '10px 14px',
              fontSize: '0.875rem',
              fontWeight: 500,
              letterSpacing: '0.1px',
              transition: 'all 0.2s cubic-bezier(0.2, 0, 0, 1)',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)'
              },
              '&.Mui-selected': {
                backgroundColor: colors.primaryContainer,
                color: colors.onPrimaryContainer,
                '&:hover': {
                  filter: 'brightness(1.08)'
                }
              }
            }
          }
        },
        MuiDivider: {
          styleOverrides: {
            root: {
              borderColor: colors.outlineVariant,
              margin: '8px 12px'
            }
          }
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              borderRadius: m3Shape.extraSmall, // 4px in M3
              color: colors.onSurface,
              transition: 'all 0.2s cubic-bezier(0.2, 0, 0, 1)',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.outlineVariant || (isDark ? 'rgba(147, 143, 153, 0.38)' : 'rgba(121, 116, 126, 0.38)'),
                transition: 'border-color 0.2s cubic-bezier(0.2, 0, 0, 1), border-width 0.15s ease'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.outline || (isDark ? '#938f99' : '#79747e')
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.primary,
                borderWidth: '2px'
              },
              '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.error,
                borderWidth: '2px'
              }
            },
            input: {
              color: colors.onSurface,
              fontFamily: FIELD_FONT,
              '&::placeholder': {
                color: colors.onSurfaceVariant,
                opacity: 0.8
              }
            }
          }
        },
        MuiFilledInput: {
          styleOverrides: {
            root: {
              borderTopLeftRadius: m3Shape.extraSmall,
              borderTopRightRadius: m3Shape.extraSmall,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              backgroundColor: colors.surfaceContainerHighest || (isDark ? '#36343b' : '#e6e0e9'),
              color: colors.onSurface,
              transition: 'background-color 0.2s cubic-bezier(0.2, 0, 0, 1)',
              '&:hover': {
                backgroundColor: colors.surfaceContainerHighest || (isDark ? '#36343b' : '#e6e0e9')
              },
              '&.Mui-focused': {
                backgroundColor: colors.surfaceContainerHighest || (isDark ? '#36343b' : '#e6e0e9')
              },
              '&:before': {
                borderColor: colors.onSurfaceVariant || (isDark ? 'rgba(202, 196, 208, 0.6)' : 'rgba(73, 69, 79, 0.6)')
              },
              '&:hover:not(.Mui-disabled):before': {
                borderColor: colors.onSurface || (isDark ? '#e6e1e5' : '#1d1b20')
              },
              '&:after': {
                borderColor: colors.primary,
                borderWidth: '2px'
              },
              '&.Mui-error:after': {
                borderColor: colors.error,
                borderWidth: '2px'
              }
            },
            input: {
              color: colors.onSurface,
              fontFamily: FIELD_FONT,
              '&::placeholder': {
                color: colors.onSurfaceVariant,
                opacity: 0.8
              }
            }
          }
        },
        MuiInputBase: { styleOverrides: { input: { fontFamily: FIELD_FONT } } },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              color: colors.onSurfaceVariant || 'text.secondary',
              fontFamily: FIELD_FONT,
              '&.Mui-focused': {
                color: colors.primary
              },
              '&.Mui-error': {
                color: colors.error
              }
            }
          }
        },
        MuiFormHelperText: {
          styleOverrides: {
            root: {
              color: colors.onSurfaceVariant || 'text.secondary',
              fontFamily: FIELD_FONT,
              fontSize: '0.75rem',
              letterSpacing: '0.4px',
              '&.Mui-error': {
                color: colors.error
              }
            }
          }
        },
        MuiInputAdornment: {
          styleOverrides: {
            root: {
              color: colors.onSurfaceVariant || 'text.secondary'
            }
          }
        },
        MuiIcon: {
          defaultProps: {
            baseClassName: 'material-symbols-outlined'
          }
        }
      }
    });
    // Narrow view (reflow): every breakpoint answers as a phone, whatever the window width, so all responsive layouts stack into one column
    // (the column itself is limited by m3.css). Components that ask the theme for a breakpoint get the same answer.
    if (narrowView) {
      const always = '@media (min-width:0px)', never = '@media (min-width:99999px)';
      const bp = built.breakpoints;
      bp.up = k => (k === 'xs' || k === 0 ? always : never);
      bp.down = k => (k === 'xs' || k === 0 ? '@media (max-width:-1px)' : always);
      bp.only = k => (k === 'xs' ? always : never);
      bp.between = (a) => (a === 'xs' || a === 0 ? always : never);
      bp.not = k => (k === 'xs' ? never : always);
    }
    return built;
  }

  // Base fallback dark theme
  const m3Colors = getM3Colors('dark', 'purple');
  const m3DarkColors = getM3Colors('dark', 'purple');
  const m3LightColors = getM3Colors('light', 'purple');
  const m3Theme = getM3Theme('dark', 'purple');

  // Secure Random Password Generator helper
  function generateRandomPassword() {
    const lowers = 'abcdefghijkmnopqrstuvwxyz';
    const uppers = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const digits = '23456789';
    const symbols = '!@#$%&*';

    const pick = (set) => set[Math.floor(Math.random() * set.length)];

    const chunk = () => {
      const chars = [pick(lowers), pick(uppers), pick(digits), pick(Math.random() > 0.4 ? symbols : lowers)];
      return chars.sort(() => Math.random() - 0.5).join('');
    };

    return `${chunk()}-${chunk()}-${chunk()}`;
  }

  // Register in global components namespace
  window.Components = window.Components || {};
  window.Components.hexToRgb = hexToRgb;
  window.Components.rgbToHex = rgbToHex;
  window.Components.generateM3Colors = generateM3Colors;
  window.Components.M3_COLOR_SCHEMES = M3_COLOR_SCHEMES;
  window.Components.m3DarkColors = m3DarkColors;
  window.Components.m3LightColors = m3LightColors;
  window.Components.m3Colors = m3Colors;
  window.Components.getM3Colors = getM3Colors;
  window.Components.m3Shape = m3Shape;
  window.Components.m3Typescale = m3Typescale;
  window.Components.m3Elevation = m3Elevation;
  window.Components.applyM3CssVariables = applyM3CssVariables;
  window.Components.resolveM3Mode = resolveM3Mode;
  window.Components.getM3Theme = getM3Theme;
  window.Components.m3Theme = m3Theme;
  window.Components.generateRandomPassword = generateRandomPassword;
})();
